import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import RiskAssessmentContent, { parseApiResponse, CategoryData } from './RiskAssessmentContent';
import './TaskRiskAssessmentView.css';
import { apiGet, apiPut } from '../../../service/api-service';
import { Toast } from 'primereact/toast';

// HIRA Team Signature interfaces
interface HIRATeamMember {
  id: string;
  full_name: string;
  title: string;
}

interface Signature {
  id: number;
  employee: string;
  employee_fullname: string;
  signature_photo: string;
  signature_s3_key: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface EmployeeSignature {
  full_name: string;
  signature_s3_key: string;
  signature_photo: string;
}
export default function TaskRiskAssessmentAttendanceView(modalProps: any) {
  const session = modalProps.session;
  const details = modalProps?.details;

  // Risk assessment data state
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const [siteName, setSiteName] = useState<string>('');
  const [assessmentDate, setAssessmentDate] = useState<string>('');
  const [reviewDate, setReviewDate] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const toast = React.useRef(null as any);

  // HIRA Team Signature State
  const [hiraTeamMembers, setHiraTeamMembers] = useState<HIRATeamMember[]>([]);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [canSign, setCanSign] = useState(false);
  const [signature, setSignature] = useState<EmployeeSignature>();
  const [hasClickedToSign, setHasClickedToSign] = useState(false);
  const [hiraExpanded, setHiraExpanded] = useState(false);

  // Fetch HIRA data from API response
  useEffect(() => {
    const fetchHiraData = async () => {
      try {
        setLoading(true);

        // Use the appropriate API endpoint
        const fullUrl = `task-risk-assessment/${details?.id}`;
        // Mock API call - replace with actual API service
        const data = await apiGet('core', fullUrl);

        // Parse the response to get category data and site info
        const { categoryData: parsedCategories, siteInfo } = parseApiResponse(data);
        setCategoryData(parsedCategories);
        setSiteName(siteInfo.name);
        setAssessmentDate(siteInfo.assessmentDate);
        setReviewDate(siteInfo.reviewDate);

        // Handle HIRA Team Members and Signatures
        if (data.content?.hira_team_members && Array.isArray(data.content.hira_team_members)) {
          setHiraTeamMembers(data.content.hira_team_members);
        }

        if (data.content?.signatures && Array.isArray(data.content.signatures)) {
          setSignatures(data.content.signatures);
        }

        if (data.content && typeof data.content.can_sign === 'boolean') {
          setCanSign(data.content.can_sign);
        }
        // Load existing signature if available
        if (data?.content?.signature) {
          setSignature(data.content.signature);
        }
      } catch (error) {
        console.error('Error fetching HIRA data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (details?.id) {
      fetchHiraData();
    }
  }, [details?.id]);

  // Handle signature submission
  const handleSignatureSubmit = async () => {
    if (!signature) {
      setHasClickedToSign(true);
      return;
    }

    try {
      const newSignature: Signature = {
        id: Date.now(),
        employee: details?.employee_id || '',
        employee_fullname: signature.full_name || '',
        signature_photo: signature.signature_photo || '',
        signature_s3_key: signature.signature_s3_key || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null
      };

      setSignatures(prev => [...prev, newSignature]);
      setHasClickedToSign(true);
    } catch (error) {
      console.error('Error signing risk assessment:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Signing Failed',
        detail: 'An error occurred while signing. Please try again.',
        life: 3000
      });
    }
  };

  // Helper functions
  const getSignatureForMember = (memberId: string): Signature | undefined => {
    return signatures.find(sig => sig.employee === memberId);
  };


  const sign = async () => {
    if (!signature) return;

    modalProps.setGlobalLoading(true);
    try {
      const fullUrl = `task-risk-assessment/${details?.id}`;
      await apiPut('core', fullUrl, {
        signature_s3_key: signature
      });

      setTimeout(() => {
        modalProps.toast.current.show({
          severity: 'success',
          summary: 'Signed Successfully',
          life: 3000
        });
        modalProps.setVisible(false);
        modalProps.setGlobalLoading(false);
        modalProps.setReload(!modalProps.reload);
      }, 500);
    } catch (error) {
      console.error('Error signing document:', error);
      modalProps.toast.current.show({
        severity: 'error',
        summary: 'Failed to Sign Document',
        life: 3000
      });
      modalProps.setGlobalLoading(false);
    }
  };

  // Render HIRA Team Sign-off Section
  const renderHiraSignoffSection = () => {
    if (hiraTeamMembers.length === 0) return null;

    // Common styles
    const signatureBoxStyles = {
      width: '200px',
      height: '80px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    const labelStyles = {
      fontSize: '0.9rem',
      color: '#666',
      marginBottom: '8px'
    };

    return (
      <>
        <Card 
          className="task-assessments-card"
          title={
            <div 
              onClick={() => setHiraExpanded(!hiraExpanded)}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span>HIRA Team Sign-off</span>
              <i className={`pi ${hiraExpanded ? 'pi-minus' : 'pi-plus'}`} />
            </div>
          }
        >
          {hiraExpanded && (
            <>
              {hiraTeamMembers.map((member, i) => {
            const memberSignature = getSignatureForMember(member.id);

            return (
              <div
                key={`${member.id}-${i}`}
                className="hira-member-signature"
                style={{
                  backgroundColor: '#f0f9f4',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '16px',
                  border: memberSignature ? '2px solid #2dd36f' : '1px solid #d7d8da'
                }}
              >
                <div style={{ marginBottom: '8px', fontSize: '1rem', fontWeight: '600', color: '#2c3e50' }}>
                  {member.title}
                </div>

                <div style={{ marginBottom: '16px', fontSize: '1.2rem', fontWeight: '700', color: '#1a1a1a' }}>
                  {member.full_name}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <div style={labelStyles}>
                      <strong>Signature:</strong>
                    </div>

                    {memberSignature?.signature_photo ? (
                      <div style={{ ...signatureBoxStyles, border: '1px solid #ddd', backgroundColor: 'white' }}>
                        <img
                          src={memberSignature?.signature_photo}
                          alt="Signature"
                          style={{ maxWidth: '100%', maxHeight: '100%' }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          ...signatureBoxStyles,
                          border: '2px dashed #ccc',
                          backgroundColor: '#f8f9fa',
                          color: '#6c757d',
                          cursor: 'default',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Awaiting signature
                      </div>
                    )}
                  </div>

                  <div>
                    <div style={labelStyles}>
                      <strong>Date:</strong>
                    </div>
                    <div style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      width: 'fit-content',
                      fontSize: '0.9rem'
                    }}>
                      {memberSignature ?
                        new Date(memberSignature.created_at).toLocaleDateString() :
                        '___________'
                      }
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
            </>
          )}
        </Card>

        <Card
          className="task-assessments-card"
          title="Attendance Sign-off"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <div style={labelStyles}>
                <strong>Signature:</strong>
              </div>

              {/* Show note when no signature is available */}
              {!signature?.signature_photo && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '12px',
                  fontSize: '0.9rem',
                  color: '#856404'
                }}>
                  <strong>📝 Note:</strong> No signature available. Please add one on the employee page to sign this assessment.
                </div>
              )}

              {hasClickedToSign ? (
                <div style={{ ...signatureBoxStyles, border: '1px solid #ddd', backgroundColor: 'white' }}>
                  <img
                    src={signature?.signature_photo}
                    alt="Signature"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    ...signatureBoxStyles,
                    border: '2px dashed #ccc',
                    backgroundColor: '#e6f3ff',
                    color: '#6c757d',
                    cursor: signature?.signature_photo ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={signature?.signature_photo ? handleSignatureSubmit : undefined}
                >
                  {signature?.signature_photo ? 'Click to add signature' : 'No signature available'}
                </div>
              )}
            </div>

            <div>
              <div style={labelStyles}>
                <strong>Date:</strong>
              </div>
              <div style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                width: 'fit-content',
                fontSize: '0.9rem'
              }}>
                {
                  new Date().toLocaleDateString()
                }
              </div>
            </div>
          </div>
        </Card>
      </>
    );
  };

  return (
    <>
      <RiskAssessmentContent
        session={session}
        details={details}
        signoffSection={renderHiraSignoffSection()}
        categoryData={categoryData}
        siteName={siteName}
        assessmentDate={assessmentDate}
        reviewDate={reviewDate}
        loading={loading}
      />
      <Button type="button" icon="" className="p-button-outlined" size="small"
        label="Complete Signature"
        disabled={!hasClickedToSign}
        style={{ display: "flex", color: "#2dd36f", marginLeft: "auto" }} onClick={() => sign()} />
      <Toast ref={toast} />
    </>
  );
}